import './KeyTopics.scss';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import usePermission from '../../../../shared/hooks/global/UsePermission';
import { Column } from 'primereact/column';
import { DataTable } from '../../../../shared/components/common/data-table/DataTable';
import { TopicsDistribution } from '../../../../shared/models/context-insights/ContextInsightsResponse';
import { SuggestedTopic } from '../../../../shared/models/projects/ProjectResponse';
import { KeywordsData } from '../../../../shared/models/tags/TagsResponse';
import { Skeleton } from 'primereact/skeleton';
import NoResults from '../../../../shared/components/common/no-results/NoResults';
import { createCustomColumn } from '../../../../shared/helpers/ColumnHelper';
import useIsMobile from '../../../../shared/hooks/global/UseIsMobile';
import { Button } from 'primereact/button';

interface KeyTopicsProps {
  topicsDistribution: TopicsDistribution[];
  suggestedTopics: SuggestedTopic[] | [];
  keyword: string | undefined;
  commonKeywords: KeywordsData[];
  isLoading: boolean;
  canExport: boolean;
  isDownloading?: boolean;
  onExportTopicsAnalysis?: () => void | Promise<void>;
}

const KeyTopics: React.FC<KeyTopicsProps> = ({
  topicsDistribution,
  suggestedTopics,
  keyword,
  commonKeywords,
  isLoading,
  canExport,
  isDownloading = false,
  onExportTopicsAnalysis,
}) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const projectsAccess = usePermission('PROJECTS');
  const currentLang = i18n.language as 'en' | 'pl';

  const handleExportTopicsAnalysis = useCallback(() => {
    if (!canExport || isDownloading) {
      return;
    }

    void onExportTopicsAnalysis?.();
  }, [canExport, isDownloading, onExportTopicsAnalysis]);

  const emptyTemplate = () => <NoResults />;

  return (
    <div className="stats__panel">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('contextAnalysis.topicsAnalysis')}
          <CustomTooltip tooltipText="contextAnalysis.topicsAnalysisTooltip" addMargin={false} />
        </div>
        {/* Eksporty analizy kontekstu są dla gościa zamknięte na backendzie (obie trasy
            `export-*` bez `allowGuest`) — przycisk kończyłby się 403. Przy pustej sekcji nie ma czego eksportować. */}
        {projectsAccess.write && topicsDistribution.length > 0 && (
        <div className="page-box__source-filter">
          <Button
            id="topics-analysis-export"
            className={`action-btn ${!canExport ? 'btn--disabled-tooltip' : ''}`}
            onClick={handleExportTopicsAnalysis}
          >
            {isDownloading ? (
              <span className="ico--loader ico--xs" />
            ) : (
              <span className={`ico--download ${!canExport ? 'ico--disabled' : ''} ico--xs`} />
            )}
          </Button>
          <CustomTooltip
            tooltipText={!canExport ? 'upgradePlanTrialExportMsg' : 'downloadStats'}
            targetRef="topics-analysis-export"
          />
        </div>
        )}
      </div>
      {isLoading ? (
        <Skeleton className="skeleton--table skeleton--table-4">
          <span />
          <span />
          <span />
          <span />
        </Skeleton>
      ) : (
        <DataTable
          stripedRows
          paginator={true}
          scrollable
          paginatorClassName="pagination"
          removableSort
          value={topicsDistribution}
          rows={10}
          emptyMessage={emptyTemplate}
          rowsPerPageOptions={[10, 25, 50]}
          tableStyle={{ minWidth: '50rem', tableLayout: 'auto' }}
          tableClassName="table"
          className="table-wrapper table-wrapper--stack"
          responsiveLayout="stack"
          currentPageReportTemplate={t('rowsNumber') + ':'}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        >
          <Column
            header={t('topic')}
            bodyClassName="table__cell--stack-title"
            field="topic"
            headerClassName="table__header"
            body={(data) => (
              <div
                style={{ display: 'flex', alignItems: 'center', fontWeight: '600' }}
                className="topic-name"
              >
                <span className="ico ico--star ico--sm" style={{ marginRight: '15px' }} />
                {
                  suggestedTopics.find(
                    (st) => st.name.toLowerCase().trim() === data.topic.toLowerCase().trim()
                  )?.[currentLang === 'pl' ? 'namePl' : 'name']
                }
              </div>
            )}
            style={{ minWidth: '100px', maxWidth: '180px' }}
          />
          <Column
            header={t('description')}
            bodyClassName="table__cell--stack-wide"
            headerClassName="table__header"
            body={(data) =>
              suggestedTopics.find(
                (st) => st.name.toLowerCase().trim() === data.topic.toLowerCase().trim()
              )?.[currentLang === 'pl' ? 'summaryPl' : 'summary']
            }
            style={{ minWidth: '200px', maxWidth: '300px' }}
          />
          {createCustomColumn({
            plainHeader: isMobile,
            sortable: true,
            field: 'count',
            headerName: 'occurrences',
            tooltip: t('contextAnalysis.keyTopicOccurrencesTooltip'),
            headerClassName: 'table__header table__header--center',
            bodyClassName: 'table__cell table__cell--center',
            style: { width: '140px' },
          })}
          {createCustomColumn({
            plainHeader: isMobile,
            sortable: true,
            field: 'percentage',
            headerName: t('topicShare'),
            headerNameSuffix: '  (%)',
            tooltip: t('contextAnalysis.topicShareTooltip'),
            headerClassName: 'table__header table__header--center',
            body: (data: TopicsDistribution) => `${data.percentage.toFixed(1)}%`,
            bodyClassName: 'table__cell table__cell--center',
            style: { width: '140px' },
          })}
          {createCustomColumn({
            plainHeader: isMobile,
            sortable: true,
            field: 'brandMentions',
            headerName: t('brandMentions', {
              brand:
                commonKeywords.find((keywordData) => keywordData.normalizedKeyword === keyword)
                  ?.keyword || keyword,
            }),
            tooltip: t('contextAnalysis.brandMentionsTooltip'),
            headerClassName: 'table__header table__header--center',
            body: (data: TopicsDistribution) => (
              <span
                className={`cell-block ${
                  data.brandMentions > 0 ? 'cell-block--success' : 'cell-block--error'
                }`}
              >
                {data.brandMentions > 0 ? data.brandMentions : 'No'}
              </span>
            ),
            bodyClassName: 'table__cell table__cell--center',
            style: { width: '140px' },
          })}
          {createCustomColumn({
            plainHeader: isMobile,
            sortable: true,
            headerName: 'brandShare',
            tooltip: 'brandShareTooltip',
            headerNameSuffix: '  (%)',
            headerClassName: 'table__header table__header--center',
            body: (data: TopicsDistribution) => {
              const brandEntry = data.topBrands.find((b) => b.keyword === keyword);
              return brandEntry && brandEntry.percentageInTopic > 0
                ? `${(brandEntry?.percentageInTopic ?? 0).toFixed(1)}%`
                : '-';
            },
            bodyClassName: 'table__cell table__cell--center',
            style: { width: '140px' },
          })}
        </DataTable>
      )}
    </div>
  );
};

export default KeyTopics;
